import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import {
  getAlertSummaryFilters,
  getAlertSummaryData,
  getAlertSummaryKPIData,
  getAlertSummarySiteAdaptionData,
  getAlertSummaryChartData,
  getAlertSummaryTrendsData
} from "Samarth Module/api/api";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  DotsThreeCircleIcon,
  ArrowCounterClockwiseIcon,
  FileXlsIcon,
  FunnelIcon,
  GraphIcon,
  TableIcon,
} from "@phosphor-icons/react";
import MetricCard from "./components/MetricCard";
import FilterSelect from "./components/FilterSelect";
import PerformanceFilterBar from "./components/PerformanceFilterBar";
import ExportButton from "./components/ExportButton";
import CommonSemiDonutChart from "./components/CommonSemiDonutChart";
import CommonTable from "./components/CommonTable";
import CommonBarGraph from "./components/CommonBarGraph";
import {
  FGCoverageData,
  maiData,
  monthlyZoneChartData,
} from "./data/dashboardData";
import CommonStackedScrollableChart from "./components/CommonStackedScrollableChart";
import CommonGroupedScrollableChart from "./components/CommonGroupedScrollableChart";
import CommonZoneLineBarChart from "./components/CommonZoneLineBarChart";
import CommonZoneTable from "./components/CommonZoneTable";
import "./RMPMSummary.css";
import Grid from "@mui/material/Grid2";
import RMPMToggleButton from "./components/RMPMToggleButton";

const INITIAL_FILTERS = {
  bg: "",
  siteCluster: "",
  siteCode: "",
  year: "",
  month: "",
  week: "",
  weekDay: "",
  jitNonJit: "",
  granularity: "",
  siteType: "",
};

const FILTER_KEY_MAP = [
  { stateKey: "bg", apiKey: "BG", emptyVal: "" },
  { stateKey: "siteCluster", apiKey: "Site Cluster", emptyVal: "" },
  { stateKey: "siteCode", apiKey: "Site Code", emptyVal: "" },
  { stateKey: "year", apiKey: "Year", emptyVal: "" },
  { stateKey: "month", apiKey: "Month", emptyVal: "" },
  { stateKey: "week", apiKey: "Alert Week", emptyVal: "" },
  { stateKey: "weekDay", apiKey: "Week Day", emptyVal: "" },
  { stateKey: "jitNonJit", apiKey: "JIT/Non-JIT", emptyVal: "" },
  { stateKey: "granularity", apiKey: "Granularity", emptyVal: "" },
  { stateKey: "siteType", apiKey: "Site Type", emptyVal: "" },
];

const LABEL_TO_KEY = {
  BG: "bg",
  "Site cluster": "siteCluster",
  "Site code": "siteCode",
  Year: "year",
  Month: "month",
  Week: "week",
  "Week Day": "weekDay",
  "JIT/Non-JIT": "jitNonJit",
  Granularity: "granularity",
  "Site type": "siteType",
};

/**
 * Every card shows the same three-state panel: a spinner while loading, then
 * either the graph or the table. Extracting it keeps those call sites free of
 * nested ternaries and keeps RMPMSummary's own complexity inside the limit.
 */
const GraphTableSwitch = ({ loading, showGraph, graph, table }) => {
  if (loading) {
    return (
      <div
        className="rmpm-graph-container"
        style={{ justifyContent: "center", alignItems: "center" }}
      >
        <CircularProgress size={32} sx={{ color: "#4D5F6E" }} />
      </div>
    );
  }
  return showGraph ? graph : table;
};

GraphTableSwitch.propTypes = {
  loading: PropTypes.bool,
  showGraph: PropTypes.bool,
  graph: PropTypes.node,
  table: PropTypes.node,
};

const RMPMSummary = () => {
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username;

  const [allFilters, setAllFilters] = useState(INITIAL_FILTERS);
  const [filterOptions, setFilterOptions] = useState({});
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const buildPayload = useCallback(() => {
    return FILTER_KEY_MAP.reduce((acc, { stateKey, apiKey, emptyVal }) => {
      if (allFilters[stateKey] !== emptyVal)
        acc[apiKey] = [allFilters[stateKey]];
      return acc;
    }, {});
  }, [allFilters]);

  const fetchFilters = useCallback(
    (payload) => {
      if (!userEmail) return;
      setIsFilterLoading(true);
      getAlertSummaryFilters(userEmail, payload, "RMPMSummary")
        .then((res) => {
          if (res?.Filters) setFilterOptions(res.Filters);
        })
        .catch((err) => console.error("Failed to fetch filter options:", err))
        .finally(() => setIsFilterLoading(false));
    },
    [userEmail],
  );

  const fetchDashboardData = useCallback(
    (payload) => {
      if (!userEmail) return;
      setIsDataLoading(true);
      Promise.all([
        getAlertSummaryData(userEmail, payload, "RMPMSummary"),
        getAlertSummaryKPIData(userEmail, payload, "RMPMSummary"),
        getAlertSummarySiteAdaptionData(userEmail, payload, "RMPMSummary"),
        getAlertSummaryChartData(userEmail, payload, "RMPMSummary"),
        getAlertSummaryTrendsData(userEmail, payload, true, "RMPMSummary")

      ])
        .then(([topData, kpiData, siteAdaptionData, chartData, trendsData]) => {
          setDashboardData({
            topData,
            kpiData,
            siteAdaptionData,
            chartData,
            trendsData,
          });
        })
        .catch((err) => console.error("Failed to fetch dashboard data:", err))
        .finally(() => setIsDataLoading(false));
    },
    [userEmail],
  );

  useEffect(() => {
    if (!userEmail) return;
    const payload = buildPayload();
    fetchFilters(payload);
    fetchDashboardData(payload);
  }, [allFilters, buildPayload, fetchFilters, fetchDashboardData]);

  // KPI Cards — wired from API data
  const kpiCalc = dashboardData?.kpiData?.KPI || {};
  const metrics = [
    {
      title: "Adoption %",
      value: (kpiCalc?.Adoptance_Count && kpiCalc?.Total > 0) ? (kpiCalc['Adoptance_Count'] / kpiCalc['Total']) * 100 : null,
      delta: null,
      infoText: "Adoption Percentage details",
    },
    {
      title: "Acceptance %",
      value: (kpiCalc?.Acceptance_Count && kpiCalc?.Total > 0) ? (kpiCalc['Acceptance_Count'] / kpiCalc['Total']) * 100 : null,
      delta: null,
      infoText: "Acceptance Percentage details",
    },
    {
      title: "MAI %",
      value: kpiCalc["MAI %"] ?? null,
      delta: null,
      infoText: "MAI Percentage details",
    },
    {
      title: "FG Coverage %",
      value: kpiCalc["FG Coverage %"] ?? null,
      delta: null,
      infoText: "FG Coverage Percentage details",
    },
  ];

  // --- Site Adoption
  const mapValueToY = (val) => {
    if (!val || val <= 0) return 0;
    if (val <= 100) return val / 100;
    if (val <= 25000) return 1 + (val - 100) / 24900;
    if (val <= 50000) return 2 + (val - 25000) / 25000;
    if (val <= 75000) return 3 + (val - 50000) / 25000;
    if (val <= 100000) return 4 + (val - 75000) / 25000;
    return 5 + (val - 100000) / 25000;
  };

  const siteAdoptionData = useMemo(() => {
    const raw = dashboardData?.siteAdaptionData?.["Site Adaption"] || {};
    return Object.entries(raw).map(([siteCode, d]) => {
      const open = d.Open ?? 0;
      const accepted = d.Accepted ?? 0;
      const rejected = d.Rejected ?? 0;

      const mapped_open = mapValueToY(open);
      const mapped_accepted = mapValueToY(accepted);
      const mapped_rejected = mapValueToY(rejected);

      return {
        label: siteCode,
        zone: "GREEN", // zone not provided by API / not in SDD
        acceptance: d?.Acceptance && d?.total_alerts ? Number(((d.Acceptance / d.total_alerts) * 100).toFixed(2)) : 0,
        adoption: d?.Adoption && d?.total_alerts ? Number(((d.Adoption / d.total_alerts) * 100).toFixed(2)) : 0,
        open,
        accepted,
        rejected,
        mapped_open,
        mapped_accepted,
        mapped_rejected,
      };
    });
  }, [dashboardData]);

  const siteAdoptionTableData = useMemo(() => {
    const columns = siteAdoptionData.map((d) => d.label);
    return {
      columns,
      rows: [
        {
          label: "Acceptance",
          values: siteAdoptionData.map((d) => d.acceptance + "%"),
          zones: siteAdoptionData.map((d) => d.zone),
        },
        {
          label: "Adoption",
          values: siteAdoptionData.map((d) => d.adoption + "%"),
          zones: siteAdoptionData.map((d) => d.zone),
        },
        {
          label: "Open",
          values: siteAdoptionData.map((d) => d.open),
          zones: siteAdoptionData.map((d) => d.zone),
        },
        {
          label: "Accepted",
          values: siteAdoptionData.map((d) => d.accepted),
          zones: siteAdoptionData.map((d) => d.zone),
        },
        {
          label: "Rejected",
          values: siteAdoptionData.map((d) => d.rejected),
          zones: siteAdoptionData.map((d) => d.zone),
        },
      ],
    };
  }, [siteAdoptionData]);

  // --- Accepted Recommendations:
  const acceptedRecommendationsData = useMemo(() => {
    if (!dashboardData) return [];
    const raw = dashboardData?.chartData?.["Recommendations"] || {};
    return Object.entries(raw).map(([name, value]) => ({ name, value }));
  }, [dashboardData]);

  // --- Root Cause Split:
  const rootCauseData = useMemo(() => {
    if (!dashboardData) return [];
    const raw = dashboardData?.chartData?.["Root Causes"] || {};
    return Object.entries(raw).map(([name, value]) => ({
      name,
      value
    }));
  }, [dashboardData]);

  // --- Top Rejection Reasons:
  const rejectionData = useMemo(() => {
    if (!dashboardData) return [];
    const raw = dashboardData?.chartData?.["Rejected Reasons"] || {};
    return Object.entries(raw).map(([name, value]) => ({
      name,
      value
    }));
  }, [dashboardData]);

  // --- Top Accepted Recommendations:
  const acceptedRecommendationTrend = useMemo(() => {
    if (!dashboardData) return [];
    const raw = dashboardData?.topData?.["Top Recommnedations"] || {};
    return Object.entries(raw).map(([week, value]) => {
      const updatedValue = { ...value };
      Object.keys(value).forEach((key) => {
        updatedValue[key] = Number(updatedValue[key]?.toFixed(2) || 0);
      });
      return { week, ...updatedValue };
    });
  }, [dashboardData]);

  // --- Top Accepted Recommendations:
  const rootCauseTrendData = useMemo(() => {
    if (!dashboardData) return [];
    const raw = dashboardData?.topData?.["Top Root_Causes"] || {};
    return Object.entries(raw).map(([week, value]) => {
      const updatedValue = { ...value };
      Object.keys(value).forEach((key) => {
        updatedValue[key] = Number(updatedValue[key]?.toFixed(2) || 0);
      });
      return { week, ...updatedValue };
    });
  }, [dashboardData]);

  const weeklyZoneChartData = useMemo(() => {
    if (!dashboardData) return [];
    const raw = dashboardData?.trendsData?.["Trends"] || {};
    return Object.entries(raw).map(([week, value]) => {
      return {
        week, acceptance: value?.Acceptance_count && value?.total_alerts > 0 ? Number(((value['Acceptance_count'] / value['total_alerts']) * 100)?.toFixed(2) || 0) : 0,
        adoption: value?.Adaptance_count && value?.total_alerts > 0 ? Number(((value['Adaptance_count'] / value['total_alerts']) * 100)?.toFixed(2) || 0) : 0,
        bar: value?.Accepted_count, zone: "GREEN"
      }
    });
  }, [dashboardData]);



  const handleFilterChange = useCallback((label, value) => {
    const key = LABEL_TO_KEY[label];
    if (!key) return;
    setAllFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSiteAdoptionFilterChange = handleFilterChange;

  // Site Adoption graph/table toggle
  const [activeSiteAdoption, setActiveSiteAdoption] = useState("graph");

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handlePerformanceFilterChange = handleFilterChange;

  const handleReset = useCallback(() => {
    setAllFilters(INITIAL_FILTERS);
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  //Merged Code Starts

  const [activeAcceptedRec, setActiveAcceptedRec] = useState("graph");
  const [activeLeft, setActiveLeft] = useState("graph");
  const [activeRight, setActiveRight] = useState("graph");
  const [activeZone, setActiveZone] = useState("graph");
  const [trendType, setTrendType] = useState("Weekly");
  const [activeMaiCoverage, setActiveMaiCoverage] = useState("graph");
  const [activeStartEnd, setActiveStartEnd] = useState("graph");
  const [activeRootCauseTrend, setActiveRootCauseTrend] = useState("graph");
  const [activeAcceptedTrend, setActiveAcceptedTrend] = useState("graph");

  const zoneData =
    trendType === "Weekly" ? weeklyZoneChartData : monthlyZoneChartData;

  // 🔥 Columns
  const zoneColumns = zoneData.map((item) => item.week);

  // 🔥 Max Bar
  const maxBar = Math.max(...zoneData.map((item) => item.bar));

  // 🔥 Table Data
  const zoneTableData = [
    {
      label: "Acceptance",
      values: zoneData.map((item) => item.acceptance + "%"),
      zones: zoneData.map((item) => item.zone),
    },
    {
      label: "Adoption",
      values: zoneData.map((item) => item.adoption + "%"),
      zones: zoneData.map((item) => item.zone),
    },
    {
      label: "Accepted %",
      values: zoneData.map(
        (item) => item.bar > 0 ? Math.round((item.bar / maxBar) * 100) + "%" : "0.00%",
      ),
      zones: zoneData.map((item) => item.zone),
    },
    {
      label: "Accepted value",
      values: zoneData.map((item) => item.bar),
      zones: zoneData.map((item) => item.zone),
    },
  ];

  // 🔥 Export Data
  const zoneExportData = zoneData.map((item) => ({
    Period: item.week,
    Acceptance: item.acceptance + "%",
    Adoption: item.adoption + "%",
    "Accepted %": Math.round((item.bar / maxBar) * 100) + "%",
    "Accepted value": item.bar,
  }));

  const acceptedPercentageData = acceptedRecommendationTrend.map((item) => {
    const updatedItem = { ...item };
    return updatedItem;
  });

  const rootCauseTrendPercentageData = rootCauseTrendData.map((item) => {
    const updatedItem = { ...item };
    return updatedItem;
  });

  const acceptedRecColumns = [
    { header: "Accepted Recommendations", accessor: "name" },
    { header: "Value", accessor: "value", align: "center" },
    { header: "Percentage %", accessor: "percentage", align: "center" },
  ];

  const totalAcceptedRec = acceptedRecommendationsData.reduce(
    (s, d) => s + d.value,
    0,
  );
  const acceptedRecTableData = acceptedRecommendationsData.map((d) => ({
    ...d,
    percentage: totalAcceptedRec
      ? ((d.value / totalAcceptedRec) * 100).toFixed(0) + "%"
      : "0%",
  }));

  const acceptedRecExportData = acceptedRecTableData.map((d) => ({
    "Accepted Recommendations": d.name,
    Value: d.value,
    "Percentage %": d.percentage,
  }));

  const rightColumns = [
    { header: "Top Rejection Reasons", accessor: "name" },
    { header: "Value", accessor: "value", align: "center" },
    { header: "Percentage %", accessor: "percentage", align: "center" },
  ];

  const leftColumns = [
    { header: "Root Causes Split", accessor: "name" },
    { header: "Value", accessor: "value", align: "center" },
    { header: "Percentage %", accessor: "percentage", align: "center" },
  ];



  const startEndColumns = [
    { header: "Weeks", accessor: "week" },
    { header: "Start of Week", accessor: "start", align: "center" },
    { header: "End of Week", accessor: "end", align: "center" },
  ];

  const maiColumns = [
    { header: "Weeks", accessor: "week" },
    { header: "Start of Week", accessor: "start", align: "center" },
    { header: "End of Week", accessor: "end", align: "center" },
  ];

  const startEndExportData = FGCoverageData.map((item) => ({
    Week: item.week,
    "Start of Week": item.start + "%",
    "End of Week": item.end + "%",
  }));

  const maiExportData = maiData.map((item) => ({
    Week: item.week,
    "Start of Week": item.start + "%",
    "End of Week": item.end + "%",
  }));

  const totalLeft = rootCauseData.reduce((sum, item) => sum + item.value, 0);

  const leftTableData = rootCauseData.map((item) => ({
    ...item,
    percentage: ((item.value / totalLeft) * 100).toFixed(1) + "%",
  }));

  const totalRight = rejectionData.reduce((sum, item) => sum + item.value, 0);

  const rightTableData = rejectionData.map((item) => ({
    ...item,
    percentage: ((item.value / totalRight) * 100).toFixed(1) + "%",
  }));

  //Merged Code Ends

  return (
    <Box
      sx={{
        backgroundColor: "#E8EEF2",
        minHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        overflowY: "auto",
        height: "90vh",
      }}
    >
      {/* Metric Cards Section */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          padding: "16px 24px 0px 24px",
          gap: "20px",
        }}
      >
        {metrics.map((metric) => (
          <MetricCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            delta={metric.delta}
            infoText={metric.infoText}
            loading={isDataLoading}
          />
        ))}
      </Box>

      {/* Filters Section */}
      <Box sx={{ padding: "0px 24px" }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FilterSelect
              label="BG"
              options={(filterOptions["BG"] || []).filter(Boolean).map(String)}
              value={allFilters.bg}
              onChange={handleFilterChange}
              disabled={isFilterLoading || isDataLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FilterSelect
              label="Site cluster"
              options={(filterOptions["Site Cluster"] || [])
                .filter(Boolean)
                .map(String)}
              value={allFilters.siteCluster}
              onChange={handleFilterChange}
              disabled={isFilterLoading || isDataLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FilterSelect
              label="Site code"
              options={(filterOptions["Site Code"] || [])
                .filter(Boolean)
                .map(String)}
              value={allFilters.siteCode}
              onChange={handleFilterChange}
              disabled={isFilterLoading || isDataLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FilterSelect
              label="JIT/Non-JIT"
              options={(filterOptions["JIT/Non-JIT"] || [])
                .filter(Boolean)
                .map(String)}
              value={allFilters.jitNonJit}
              onChange={handleFilterChange}
              disabled={isFilterLoading || isDataLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FilterSelect
              label="Site type"
              options={(filterOptions["Site Type"] || [])
                .filter(Boolean)
                .map(String)}
              value={allFilters.siteType}
              onChange={handleFilterChange}
              disabled={isFilterLoading || isDataLoading}
            />
          </Grid>
          <Grid
            size={{ xs: 12, md: 2 }}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {isFilterLoading && (
                <CircularProgress
                  size={18}
                  sx={{ color: "#4D5F6E", flexShrink: 0 }}
                />
              )}
              <IconButton
                onClick={handleReset}
                disabled={isFilterLoading || isDataLoading}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  border: "1px solid #D9E1E7",
                  padding: "8px",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <ArrowCounterClockwiseIcon size={20} color="#4D5F6E" />
              </IconButton>

              <IconButton
                onClick={handleClick}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  border: "1px solid #D9E1E7",
                  padding: "8px",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <DotsThreeCircleIcon size={20} color="#4D5F6E" />
              </IconButton>

              <Button
                variant="contained"
                startIcon={<FileXlsIcon size={20} color="#2B911C" />}
                sx={{
                  backgroundColor: "#FFFFFF",
                  color: "#1A2025",
                  textTransform: "none",
                  fontWeight: 400,
                  boxShadow: "none",
                  border: "1px solid #D9E1E7",
                  borderRadius: "8px",
                  padding: "8px 14px",
                }}
              >
                Export
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      overflow: "visible",
                      filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                      mt: 1.5,
                      "&:before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={handleClose}>
                  <FunnelIcon sx={{ marginRight: "10px", color: "#5d6773" }} />
                  <Typography variant="body2">More Filters</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Performance Analysis Section */}
      <Box
        sx={{
          backgroundColor: "#F1F5F7",
          borderRadius: "8px",
          padding: "16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: 20,
              fontWeight: 500,
              color: "#1A2025",
            }}
          >
            Performance Analysis
          </Typography>
          <PerformanceFilterBar
            disabled={isFilterLoading || isDataLoading}
            filters={[
              {
                label: "Year",
                options: (filterOptions["Year"] || []).map(String),
                value: allFilters.year,
                onChange: handlePerformanceFilterChange,
              },
              {
                label: "Month",
                options: (filterOptions["Month"] || []).map(String),
                value: allFilters.month,
                onChange: handlePerformanceFilterChange,
              },
              {
                label: "Week",
                options: (filterOptions["Alert Week"] || []).map(String),
                value: allFilters.week,
                onChange: handlePerformanceFilterChange,
              },
              {
                label: "Week Day",
                options: (filterOptions["Week Day"] || []).map(String),
                value: allFilters.weekDay,
                onChange: handlePerformanceFilterChange,
              },
            ]}
          />
        </Box>
        {/* Site Adoption  */}
        <Box className="rmpm-card">
          <Box className="rmpm-card-header">
            <Typography variant="h6">Site Adoption</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "nowrap",
              }}
            >
              <FilterSelect
                compact
                label="Granularity"
                options={(filterOptions["Granularity"] || [])
                  .filter(Boolean)
                  .map(String)}
                value={allFilters.granularity}
                onChange={handleSiteAdoptionFilterChange}
                disabled={isFilterLoading || isDataLoading}
              />
              <RMPMToggleButton
                options={[
                  { icon: <GraphIcon size={20} />, value: "graph" },
                  { icon: <TableIcon size={20} />, value: "table" },
                ]}
                value={activeSiteAdoption}
                onChange={setActiveSiteAdoption}
              />
              <ExportButton
                data={siteAdoptionTableData.rows.map((row) =>
                  Object.fromEntries([
                    ["Metric", row.label],
                    ...siteAdoptionTableData.columns.map((col, i) => [
                      col,
                      row.values[i],
                    ]),
                  ]),
                )}
                fileName="site-adoption.xls"
              />
            </Box>
          </Box>

          <GraphTableSwitch
            loading={isDataLoading}
            showGraph={activeSiteAdoption === "graph"}
            graph={(
              <div className="rmpm-graph-container">
                <CommonZoneLineBarChart
                  data={siteAdoptionData}
                  xKey="label"
                  yAxisDomain={[0, 7.5]}
                  yAxisTicks={[0, 1, 2, 3, 4, 5]}
                  yAxisTickFormatter={(val) => {
                    const labels = {
                      0: "0",
                      1: "100",
                      2: "25000",
                      3: "50000",
                      4: "75000",
                      5: "100000",
                    };
                    return labels[val] || "";
                  }}
                  lineAxisDomain={[-200, 100]}
                  hideLeftAxis={false}
                  stackedBars={[
                    {
                      dataKey: "mapped_open",
                      originalKey: "open",
                      color: "#354ACD",
                      label: "Open",
                      stackId: "open",
                      radius: [4, 4, 0, 0],
                      getTopLabel: (p) =>
                        p.open >= 1000
                          ? (p.open / 1000).toFixed(1) + "K"
                          : p.open,
                    },
                    {
                      dataKey: "mapped_accepted",
                      originalKey: "accepted",
                      color: "#34A853",
                      label: "Accepted",
                      stackId: "accepted",
                      radius: [4, 4, 0, 0],
                      getTopLabel: (p) =>
                        p.accepted >= 1000
                          ? (p.accepted / 1000).toFixed(1) + "K"
                          : p.accepted,
                    },
                    {
                      dataKey: "mapped_rejected",
                      originalKey: "rejected",
                      color: "#E91E63",
                      label: "Rejected",
                      stackId: "rejected",
                      radius: [4, 4, 0, 0],
                      getTopLabel: (p) =>
                        p.rejected >= 1000
                          ? (p.rejected / 1000).toFixed(1) + "K"
                          : p.rejected,
                    },
                  ]}
                />
              </div>
            )}
            table={(
              <div className="rmpm-table-container">
                <CommonZoneTable
                  data={siteAdoptionTableData.rows}
                  columns={siteAdoptionTableData.columns}
                  rowHeaderLabel="Order"
                />
              </div>
            )}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, md: 4 }} className="rmpm-card">
            <Box className="rmpm-card-header">
              <Typography variant="h6">Accepted Recommendations</Typography>
              <Box className="rmpm-card-header-actions">
                <RMPMToggleButton
                  options={[
                    { icon: <GraphIcon size={20} />, value: "graph" },
                    { icon: <TableIcon size={20} />, value: "table" },
                  ]}
                  value={activeAcceptedRec}
                  onChange={setActiveAcceptedRec}
                />
                <ExportButton
                  data={acceptedRecExportData}
                  fileName="accepted-recommendations.xls"
                />
              </Box>
            </Box>
            <GraphTableSwitch
              loading={isDataLoading}
              showGraph={activeAcceptedRec === "graph"}
              graph={(
                <div className="rmpm-graph-container">
                  <CommonBarGraph
                    data={acceptedRecommendationsData}
                    color="#2563EB"
                    horizontal
                  />
                </div>
              )}
              table={(
                <div className="rmpm-table-container">
                  <CommonTable
                    data={acceptedRecTableData}
                    columns={acceptedRecColumns}
                  />
                </div>
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }} className="rmpm-card">
            <Box className="rmpm-card-header">
              <Typography variant="h6">Root Causes Split</Typography>

              <Box className="rmpm-card-header-actions">
                <RMPMToggleButton
                  options={[
                    { icon: <GraphIcon />, value: "graph" },
                    { icon: <TableIcon />, value: "table" },
                  ]}
                  value={activeLeft}
                  onChange={setActiveLeft}
                />

                <ExportButton data={leftTableData} fileName="root-causes.xls" />
              </Box>
            </Box>

            <GraphTableSwitch
              loading={isDataLoading}
              showGraph={activeLeft === "graph"}
              graph={(
                <Box className="rmpm-graph-container">
                  <CommonSemiDonutChart data={rootCauseData} />
                </Box>
              )}
              table={(
                <Box className="rmpm-table-container">
                  <CommonTable data={leftTableData} columns={leftColumns} />
                </Box>
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }} className="rmpm-card">
            <Box className="rmpm-card-header">
              <Typography variant="h6">Top Rejection Reasons</Typography>

              <Box className="rmpm-card-header-actions">
                <RMPMToggleButton
                  options={[
                    { icon: <GraphIcon />, value: "graph" },
                    { icon: <TableIcon />, value: "table" },
                  ]}
                  value={activeRight}
                  onChange={setActiveRight}
                />
                <ExportButton
                  data={rightTableData}
                  fileName="issue-category.xls"
                />
              </Box>
            </Box>
            <GraphTableSwitch
              loading={isDataLoading}
              showGraph={activeRight === "graph"}
              graph={(
                <div className="rmpm-graph-container">
                  <CommonBarGraph data={rejectionData} />
                </div>
              )}
              table={(
                <div className="rmpm-table-container">
                  <CommonTable data={rightTableData} columns={rightColumns} />
                </div>
              )}
            />
          </Grid>
        </Grid>
        {/* Merged Code Ends */}
      </Box>
      {/* Additional sections with similar structure can be added here */}
      <Box
        sx={{
          backgroundColor: "#F1F5F7",
          borderRadius: "8px",
          padding: "16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: 20,
              fontWeight: 500,
              color: "#1A2025",
            }}
          >
            Trend Monitoring
          </Typography>
          <Box className="rmpm-card-header-actions">
            <RMPMToggleButton
              options={[
                { title: "Weekly", value: "Weekly" },
                { title: "Monthly", value: "Monthly" },
              ]}
              value={trendType}
              onChange={setTrendType}
            />
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12 }} className="rmpm-card">
            <Box className="rmpm-card-header">
              <Typography variant="h6">{trendType} Trend</Typography>

              <Box className="rmpm-card-header-actions">
                <RMPMToggleButton
                  options={[
                    { icon: <GraphIcon size={20} />, value: "graph" },
                    { icon: <TableIcon size={20} />, value: "table" },
                  ]}
                  value={activeZone}
                  onChange={setActiveZone}
                />
                <ExportButton
                  data={zoneExportData}
                  fileName="weekly-acceptance-adoption.xls"
                />
              </Box>
            </Box>

            <GraphTableSwitch
              loading={isDataLoading}
              showGraph={activeZone === "graph"}
              graph={(
                <div className="rmpm-graph-container">
                  <CommonZoneLineBarChart
                    data={zoneData}
                    lineConfig={{
                      acceptance: {
                        stroke: "#FFDCC5",
                        dotFill: "#FFB98A",
                        dashed: false,
                        labelColor: "#A34100",
                      },
                      adoption: {
                        stroke: "#6DEFFF",
                        dotFill: "#6DEFFF",
                        dashed: true,
                        labelColor: "#00606C",
                      },
                    }}
                  />
                </div>
              )}
              table={(
                <div className="rmpm-table-container">
                  <CommonZoneTable data={zoneTableData} columns={zoneColumns} />
                </div>
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }} className="rmpm-card">
            <Box className="rmpm-card-header">
              <Typography variant="h6">MAI %</Typography>
              <div className="rmpm-card-header-actions">
                <RMPMToggleButton
                  options={[
                    { icon: <GraphIcon size={20} />, value: "graph" },
                    { icon: <TableIcon size={20} />, value: "table" },
                  ]}
                  value={activeMaiCoverage}
                  onChange={setActiveMaiCoverage}
                />
                <ExportButton
                  data={maiExportData}
                  fileName="mai-coverage-trend.xls"
                />
              </div>
            </Box>

            {activeMaiCoverage === "graph" ? (
              <div className="rmpm-graph-container">
                <CommonGroupedScrollableChart
                  data={maiData}
                  xKey="week"
                  bars={[
                    {
                      dataKey: "start",
                      color: "#b6f7ff",
                      label: "Start of Week",
                    },
                    { dataKey: "end", color: "#008090", label: "End of Week" },
                  ]}
                />
              </div>
            ) : (
              <div className="rmpm-table-container">
                <CommonTable data={maiData} columns={maiColumns} />
              </div>
            )}
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }} className="rmpm-card">
            <Box className="rmpm-card-header">
              <Typography variant="h6">FG Coverage</Typography>
              <div className="rmpm-card-header-actions">
                <RMPMToggleButton
                  options={[
                    { icon: <GraphIcon size={20} />, value: "graph" },
                    { icon: <TableIcon size={20} />, value: "table" },
                  ]}
                  value={activeStartEnd}
                  onChange={setActiveStartEnd}
                />

                <ExportButton
                  data={startEndExportData}
                  fileName="start-end-week-trend.xls"
                />
              </div>
            </Box>

            {activeStartEnd === "graph" ? (
              <div className="rmpm-graph-container">
                <CommonGroupedScrollableChart
                  data={FGCoverageData}
                  xKey="week"
                  bars={[
                    {
                      dataKey: "start",
                      color: "#CFBAF2",
                      label: "Start of Week",
                    },
                    { dataKey: "end", color: "#8652DF", label: "End of Week" },
                  ]}
                />
              </div>
            ) : (
              <div className="rmpm-table-container">
                <CommonTable columns={startEndColumns} />
              </div>
            )}
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }} className="rmpm-card">
            <Box className="rmpm-card-header">
              <Typography variant="h6">Root causes trend</Typography>
              <Box className="rmpm-card-header-actions">
                <RMPMToggleButton
                  options={[
                    { icon: <GraphIcon size={20} />, value: "graph" },
                    { icon: <TableIcon size={20} />, value: "table" },
                  ]}
                  value={activeRootCauseTrend}
                  onChange={setActiveRootCauseTrend}
                />

                <ExportButton
                  data={rootCauseTrendPercentageData}
                  fileName="root-cause-trend.xls"
                />
              </Box>
            </Box>

            <GraphTableSwitch
              loading={isDataLoading}
              showGraph={activeRootCauseTrend === "graph"}
              graph={(
                <div className="rmpm-graph-container">
                  <CommonStackedScrollableChart
                    data={rootCauseTrendData}
                    xKey="week"
                    stackBars={[
                      { dataKey: "PlanVariation", color: "#85c2ff" },
                      { dataKey: "POIssue", color: "#cfbaf2" },
                      { dataKey: "SupplierIssue", color: "#f3aed3" },
                      { dataKey: "QualityIssue", color: "#9ae98e" },
                      { dataKey: "Others", color: "#0066cc" },
                    ]}
                  />
                </div>
              )}
              table={(
                <div className="rmpm-table-container">
                  {rootCauseTrendPercentageData?.length > 0 ? <CommonTable
                    data={rootCauseTrendPercentageData}
                    columns={Object.keys(rootCauseTrendPercentageData[0]).map((key) => ({
                      header: key === "week" ? "Week" : key.toUpperCase(),
                      accessor: key,
                    }))}
                  /> : <></>}
                </div>
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }} className="rmpm-card">
            <Box className="rmpm-card-header">
              <Typography variant="h6">
                Accepted Recommendation Trend
              </Typography>
              <div className="rmpm-card-header-actions">
                <RMPMToggleButton
                  options={[
                    { icon: <GraphIcon size={20} />, value: "graph" },
                    { icon: <TableIcon size={20} />, value: "table" },
                  ]}
                  value={activeAcceptedTrend}
                  onChange={setActiveAcceptedTrend}
                />

                <ExportButton
                  data={acceptedPercentageData}
                  fileName="accepted-recommendation-trend.xls"
                />
              </div>
            </Box>

            <GraphTableSwitch
              loading={isDataLoading}
              showGraph={activeAcceptedTrend === "graph"}
              graph={(
                <div className="rmpm-graph-container">
                  <CommonStackedScrollableChart
                    data={acceptedPercentageData}
                    xKey="week"
                    stackBars={[
                      { dataKey: "IUT", color: "#85c2ff" },
                      { dataKey: "PR to PO", color: "#cfbaf2" },
                      { dataKey: "Alternate Vendor", color: "#f3aed3" },
                      {
                        dataKey: "Expedite Shipment",
                        color: "#9ae98e",
                      },
                      { dataKey: "Other Recommendation", color: "#0066cc" },
                    ]}
                  />
                </div>
              )}
              table={(
                <div className="rmpm-table-container">
                  {acceptedPercentageData?.length > 0 ? (
                    <CommonTable
                      data={acceptedPercentageData}
                      columns={Object.keys(acceptedPercentageData[0]).map((key) => ({
                        header: key === "week" ? "Week" : key.toUpperCase(),
                        accessor: key,
                      }))}
                    />
                  ) : <></>}
                </div>
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default RMPMSummary;
